import { z } from 'zod';
import { uuidSchema } from './validation.js';
/**
 * BLU-711 A2 — stocktake proposal + approval surface.
 *
 * A 3PL owner's stock count against a CLIENT's rows at a `review`-mode
 * shared location lands as a `stocktake_proposals` row (+ `_lines`) instead
 * of writing straight through `bulkStocktake`. The client reviews, resolves
 * any guardrail flags, and approves — which routes through the EXISTING
 * `bulkStocktake` service path so the 30-day undo and Logs -> Bulk Imports
 * variance detail keep working unchanged. See
 * `.planning/findings/2026-07-31-stocktake-approval-a2-handover.md` §3.
 */
// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------
export const stocktakeProposalStatusSchema = z.enum([
    'pending',
    'waiting_on_3pl',
    'approved',
    'rejected',
    'applied',
]);
export const stocktakeLineDecisionSchema = z.enum(['accept', 'recount', 'reject']);
export const stocktakeLineFlagSchema = z.enum(['mirror', 'drop', 'uncounted']);
/**
 * The reviewer's action when stepping through a flagged line. 'keep' and
 * 'asked' leave `decision` at its default ('accept'); 'recount' / 'reject'
 * also set the line's `decision` to match.
 */
export const stocktakeFlagResolutionSchema = z.enum(['keep', 'recount', 'reject', 'asked']);
export const stocktakeProposalTabSchema = z.enum(['pending', 'waiting', 'history']);
/** Per-junction opt-in (`location_client_organizations.stocktake_review_mode`). */
export const stocktakeReviewModeSchema = z.enum(['auto_apply', 'review']);
// ---------------------------------------------------------------------------
// Row / DTO shapes
// ---------------------------------------------------------------------------
export const stocktakeProposalLineRowSchema = z.object({
    id: uuidSchema,
    proposalId: uuidSchema,
    skuId: uuidSchema,
    sku: z.string().nullable(),
    title: z.string().nullable(),
    size: z.string().nullable(),
    color: z.string().nullable(),
    expectedQty: z.number().int(),
    /** NULL means NOT COUNTED — never written to sku_inventory. See the DB
     *  column comment on stocktake_proposal_lines.counted_qty. */
    countedQty: z.number().int().nullable(),
    decision: stocktakeLineDecisionSchema,
    flag: stocktakeLineFlagSchema.nullable(),
    flagMeta: z.unknown().nullable(),
});
const stocktakeProposalAggregateFields = {
    skuCount: z.number().int(),
    notCountedCount: z.number().int(),
    increaseCount: z.number().int(),
    decreaseCount: z.number().int(),
    /** Units gained across lines counted above expected (tiles show units). */
    increaseUnits: z.number().int(),
    /** Units lost across lines counted below expected (positive number). */
    decreaseUnits: z.number().int(),
    netChange: z.number().int(),
    /** Mirror pairs (one card per pair) + drops + (1 if any line is uncounted). */
    flagsCount: z.number().int(),
    /** Same card grouping as `flagsCount` (mirror pairs + drops), but counting
     *  only UNRESOLVED cards, and excluding the uncounted card entirely — a
     *  partial-but-otherwise-clean count is one-click approvable. Mirrors
     *  `flag-cards.ts#buildFlagCards`'s pair-resolution rule: a mirror pair only
     *  counts as resolved once BOTH lines carry a `flag_meta.resolved` stamp. */
    unresolvedFlagsCount: z.number().int(),
};
export const stocktakeProposalSummarySchema = z.object({
    proposalId: uuidSchema,
    locationId: uuidSchema,
    locationName: z.string().nullable(),
    status: stocktakeProposalStatusSchema,
    ownerOrganizationId: uuidSchema,
    clientOrganizationId: uuidSchema,
    /** The OTHER org from the caller's point of view — owner sees the client
     *  and vice versa. Convenience so the list UI never has to branch. */
    counterpartyOrganizationId: uuidSchema,
    filename: z.string().nullable(),
    countedAt: z.string().nullable(),
    countedBy: uuidSchema.nullable(),
    /** Display name of the counter (profiles first+last, else email) — resolved
     *  server-side so the UI never renders a bare uuid. */
    countedByName: z.string().nullable(),
    submittedAt: z.string().nullable(),
    resolvedAt: z.string().nullable(),
    appliedBatchId: uuidSchema.nullable(),
    createdAt: z.string(),
    ...stocktakeProposalAggregateFields,
});
/** Outcome of the post-approve Shopify push sweep, persisted to
 *  `stocktake_proposals.push_summary` (jsonb) so a failed/contended push is
 *  visible after the fact and `stocktakes.retryPush` knows exactly which
 *  SKUs to retry. `failedSkuIds` = hard errors PLUS SKUs still contended
 *  after all retries — the two kinds `retryPush` treats identically. */
export const stocktakePushSummarySchema = z.object({
    pushed: z.number().int(),
    noop: z.number().int(),
    contention: z.number().int(),
    failed: z.number().int(),
    total: z.number().int(),
    completedAt: z.string(),
    failedSkuIds: z.array(uuidSchema),
});
export const stocktakeProposalDetailSchema = stocktakeProposalSummarySchema.extend({
    lines: z.array(stocktakeProposalLineRowSchema),
    /** Outcome of the post-approve Shopify push sweep. NULL means either "the
     *  sweep has not finished yet" (it runs fire-and-forget off approve) or
     *  "this count never pushed" — the applied-state UI reads `status` and this
     *  field together, and polls briefly while it is null on an applied count. */
    pushSummary: stocktakePushSummarySchema.nullable(),
});
/** One line whose ACTUAL stock moved between the count being submitted and the
 *  client approving it: `expectedQty` is the snapshot taken at submit time,
 *  `currentStock` is what the shelf says now, `countedQty` is what approving
 *  would write over it. Carried on the CONFLICT thrown by `stocktakes.approve`
 *  as `cause: { code: 'STOCKTAKE_DRIFT', driftedLines }` (forwarded to the
 *  client by the tRPC errorFormatter's FORWARDABLE_CAUSE_CODES allowlist). */
export const stocktakeDriftedLineSchema = z.object({
    skuId: uuidSchema,
    sku: z.string(),
    expectedQty: z.number().int(),
    currentStock: z.number().int(),
    countedQty: z.number().int(),
});
// ---------------------------------------------------------------------------
// Endpoint inputs
// ---------------------------------------------------------------------------
export const listStocktakeProposalsSchema = z.object({
    tab: stocktakeProposalTabSchema,
    locationId: uuidSchema.optional(),
});
export const getStocktakeProposalDetailSchema = z.object({
    proposalId: uuidSchema,
});
/** A proposal can never carry more lines than the bulk stocktake that
 *  created it, so this mirrors BULK_STOCKTAKE_MAX_ROWS (inventory.ts) rather
 *  than importing it — kept as its own named constant per the "own constant,
 *  different risk profile" rationale already established for that value. */
export const STOCKTAKE_PROPOSAL_MAX_LINES = 2000;
export const setStocktakeLineDecisionsSchema = z.object({
    proposalId: uuidSchema,
    decisions: z
        .array(z.object({
        lineId: uuidSchema,
        decision: stocktakeLineDecisionSchema,
    }))
        .min(1)
        .max(STOCKTAKE_PROPOSAL_MAX_LINES),
});
export const resolveStocktakeFlagSchema = z.object({
    proposalId: uuidSchema,
    lineId: uuidSchema,
    resolution: stocktakeFlagResolutionSchema,
});
export const approveStocktakeProposalSchema = z.object({
    proposalId: uuidSchema,
    /** The client has been shown a stock-drift warning (current stock at
     *  approval time no longer matches the expected_qty snapshotted at submit
     *  time) and wants to proceed anyway. Omitted/false ⇒ a drift blocks
     *  approval with a CONFLICT carrying the drifted lines. */
    acknowledgeDrift: z.boolean().optional(),
});
export const retryStocktakePushSchema = z.object({
    proposalId: uuidSchema,
});
export const rejectStocktakeProposalSchema = z.object({
    proposalId: uuidSchema,
});
export const setStocktakeWaitingSchema = z.object({
    proposalId: uuidSchema,
    waiting: z.boolean(),
});
// ---------------------------------------------------------------------------
// Messaging (Phase 6) — cross-org thread on `comments`, entity_type
// 'stocktake_proposal'. Mirrors order-messages' schema shapes. See
// `.planning/findings/2026-07-31-stocktake-approval-a2-handover.md` §7 and
// `modules/stocktake-messages/stocktake-messages.service.ts`.
// ---------------------------------------------------------------------------
/** A message may reference the specific stock lines it is about — stored as
 *  SKU CODES (not row ids) so a reference survives catalog rebuilds. See
 *  `20260804130000_comments_skus_references.sql`. */
export const STOCKTAKE_MESSAGE_MAX_SKUS = 200;
export const listStocktakeMessagesSchema = z.object({
    proposalId: uuidSchema,
});
export const postStocktakeMessageSchema = z.object({
    proposalId: uuidSchema,
    body: z.string().trim().min(1).max(4000),
    skus: z.array(z.string().trim().min(1).max(120)).max(STOCKTAKE_MESSAGE_MAX_SKUS).optional(),
});
export const markStocktakeThreadReadSchema = z.object({
    proposalId: uuidSchema,
});
export const stocktakeMessagesInboxMetaSchema = z.object({
    proposalIds: z.array(uuidSchema).max(100),
});
/** 'none' = in-app only; 'slack' = ping the configured channel via this org's
 *  Threadbot; 'copy' = client-side "copy link" affordance (same ping text,
 *  no Slack post) — see `stocktakeMessagesNotifyMode` on organization.ts. */
export const stocktakeNotifyModeSchema = z.enum(['none', 'slack', 'copy']);
/**
 * `proposalId` is accepted for wire-compatibility with the (already-built)
 * frontend `useStocktakeNotifyConfig(proposalId, ...)` hook, which gates its
 * query/mutation on "a proposal is open" — the setting itself is ORG-WIDE
 * (every org configures its own outbound-ping channel once, owner-org
 * identity model, see stocktake-slack-notifier.ts), so the handler accepts
 * but does not otherwise use this field.
 */
export const getStocktakeNotifyConfigSchema = z.object({
    proposalId: uuidSchema,
});
export const setStocktakeNotifyConfigSchema = z
    .object({
    proposalId: uuidSchema,
    mode: stocktakeNotifyModeSchema,
    channel: z.string().trim().min(1).max(200).optional(),
})
    .refine((v) => v.mode !== 'slack' || Boolean(v.channel), {
    message: 'A channel is required when notifying via Slack',
    path: ['channel'],
});
